import { useEffect, useState } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { showNavigableRoute, createStartPointLink } from './utils.js';

// ✅ Tutti gli stili insieme - icona base + animazioni
const markerStyles = `
    .ai-marker-icon {
        /* Stili base dell'icona */
    }
    
    .ai-marker-icon > div {
        width: 30px;
        height: 30px;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        border: 2px solid white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 3px 8px rgba(102, 126, 234, 0.6);
        cursor: pointer;
        transition: transform 0.2s ease;
    }
    
    /* Animazioni */
    @keyframes ai-marker-pulse {
        0%, 100% {
            transform: scale(1) rotate(0deg);
            filter: drop-shadow(0 0 0 rgba(76, 175, 80, 0));
        }
        50% {
            transform: scale(1.15) rotate(-5deg);
            filter: drop-shadow(0 0 8px rgba(76, 175, 80, 0.6));
        }
    }
    
    @keyframes ai-marker-think {
        0%, 100% { transform: rotate(0deg); }
        25% { transform: rotate(-8deg); }
        75% { transform: rotate(8deg); }
    }
    
    /* Classi per animazioni */
    .ai-marker-icon.ai-marker-hover > div {
        animation: ai-marker-pulse 0.6s ease-in-out infinite;
    }
    
    .ai-marker-icon.ai-marker-thinking > div {
        animation: ai-marker-think 1s ease-in-out infinite;
    }
`;

// Inietta gli stili nel documento (una volta sola)
if (typeof document !== 'undefined' && !document.getElementById('ai-marker-styles')) {
    const styleSheet = document.createElement('style');
    styleSheet.id = 'ai-marker-styles';
    styleSheet.textContent = markerStyles;
    document.head.appendChild(styleSheet);
}

// ✅ Icona con HTML minimo - gli stili vengono da CSS
const AIBrainMarkerIcon = L.divIcon({
    html: `<div><span style="font-size: 18px; line-height: 1;">🧠</span></div>`,
    className: 'ai-marker-icon',
    iconSize: [30, 30],
    iconAnchor: [15, 30],
    popupAnchor: [0, -30]
});

// Funzione per estrarre l'ID con la stessa logica dello script di build
function getFeatureId(feature) {
    if (feature.id) return feature.id.toString();
    if (feature.properties?.['@id']) return feature.properties['@id'];
    if (feature.properties?.['@relations']?.[0]) {
        return `relation/${feature.properties['@relations'][0]}`;
    }
    return null;
}


export default function AIMarkers({ features, summaries }) {
    const map = useMap();


    useEffect(() => {
        if (!features || !map || !summaries) {
            if (!summaries) console.log('⏳ Attendendo caricamento summaries...');
            return;
        }

        console.log('🔍 AIMarkers: Processando', features.length, 'features');

        const markers = [];
        const processedFeatures = new Set();

        features.forEach((feature, idx) => {
            const props = feature.properties || {};
            const geometryType = feature.geometry?.type;

            // Estrai ID con logica robusta
            const featureId = getFeatureId(feature);
            if (!featureId) {
                console.warn('⚠️ Impossibile estrarre ID per:', props.name);
                return;
            }

            // Verifica se esiste una summary per questo percorso
            const summary = summaries[featureId];
            if (!summary) {
                // Non creare marker se non c'è summary
                return;
            }

            if (processedFeatures.has(featureId)) {
                console.log('⏭️ Feature già processata:', props.name);
                return;
            }
            processedFeatures.add(featureId);

            // Estrai coordinate di inizio
            let startCoords = null;
            if (geometryType === 'Point') {
                startCoords = feature.geometry.coordinates;
            } else if (geometryType === 'LineString') {
                startCoords = feature.geometry.coordinates?.[0];
            } else if (geometryType === 'MultiLineString') {
                startCoords = feature.geometry.coordinates?.[0]?.[0];
            }

            if (!startCoords || startCoords.length < 2) {
                console.warn('⚠️ Coordinate non valide per:', props.name);
                return;
            }

            const lat = Number(startCoords[1]);
            const lon = Number(startCoords[0]);
            if (isNaN(lat) || isNaN(lon)) {
                console.error('❌ Coordinate NaN:', { lat, lon, feature: props.name });
                return;
            }

            console.log(`✅ ${idx + 1}. Creando marker AI con summary:`, {
                name: props.name,
                id: featureId,
                type: geometryType,
                coords: { lat, lon },
                summaryLength: summary.length
            });

            try {
                const marker = L.marker([lat, lon], {
                    icon: AIBrainMarkerIcon,
                    zIndexOffset: 1000,
                    title: '🤖 ' + (props.name || 'Sentiero con AI')
                });

                marker.addTo(map);

                // ✅ Aggiungi animazione hover sull'icona stessa
                marker.on('mouseover', function () {
                    const icon = this._icon;
                    if (icon) {
                        icon.classList.remove('ai-marker-hover', 'ai-marker-thinking');
                        void icon.offsetWidth;
                        icon.classList.add('ai-marker-hover');
                    }
                });

                marker.on('mouseout', function () {
                    const icon = this._icon;
                    if (icon) {
                        icon.classList.remove('ai-marker-hover');
                    }
                });

                // ✅ Animazione continua quando il popup è aperto
                marker.on('popupopen', function () {
                    const icon = this._icon;
                    if (icon) {
                        icon.classList.add('ai-marker-thinking');
                    }
                });

                marker.on('popupclose', function () {
                    const icon = this._icon;
                    if (icon) {
                        icon.classList.remove('ai-marker-thinking');
                    }
                });

                const labelTitle = props.name ? props.name : `${props.from || ''}→${props.to || ''}`;

                // ✅ DISTINGUI TRA POINT E LINESTRING/MULTILINESTRING
                let navigationButtons = '';

                if (geometryType === 'Point') {
                    // Point: solo link al punto su Google Maps
                    const pointUrl = `https://www.google.com/maps/search/?api=1&query=${lat},${lon}`;
                    navigationButtons = `
                        <div style="margin: 8px 0;">
                            <strong>📍 Punto di interesse</strong><br/>
                            <a href="${pointUrl}" target="_blank" rel="noopener noreferrer" 
                            style="display: inline-block; padding: 6px 12px; background: #4285F4; color: white; text-decoration: none; border-radius: 4px; margin: 4px 0; cursor: pointer; font-size: 13px;">
                                📍 Vedi su Google Maps
                            </a>
                        </div>
                    `;
                } else {
                    // LineString/MultiLineString: bottoni completi
                    const googleMapsStartUrl = createStartPointLink(feature);
                    navigationButtons = `
                        <div style="margin: 8px 0;">
                            <strong>Vai a:</strong><br/>
                            <a href="${googleMapsStartUrl}" target="_blank" rel="noopener noreferrer" 
                            style="display: inline-block; padding: 6px 12px; background: #4285F4; color: white; text-decoration: none; border-radius: 4px; margin: 4px 0; cursor: pointer; font-size: 13px;">
                                📍 Inizio Percorso
                            </a><br/>
                            <button 
                                style="
                                    display: inline-block;
                                    padding: 6px 12px;
                                    background: #4CAF50;
                                    color: white;
                                    border: none;
                                    border-radius: 4px;
                                    font-size: 13px;
                                    font-weight: 500;
                                    cursor: pointer;
                                    margin: 4px 0;
                                "
                                onclick="window.viewRouteDetail('${featureId}');"
                            >
                                🗺️ Vedi percorso
                            </button>
                        </div>
                    `;
                }

                // Popup con summary già disponibile (no loading)
                const popupContent = `
                    <div style="
                        min-width: 280px; 
                        max-width: min(450px, 90vw); 
                        max-height: 80vh; 
                        overflow-y: auto;
                        font-size: 14px;
                    ">
                        <strong style="font-size: 16px;">${labelTitle}</strong><br/>
                        
                        ${navigationButtons}
                        
                        <hr style="margin: 10px 0; border: none; border-top: 1px solid #ddd;">
                        
                        <div style="margin: 8px 0;">
                            <strong style="font-size: 14px;">📄 Descrizione:</strong>
                            <div style="
                                max-height: 200px; 
                                overflow-y: auto; 
                                margin: 6px 0; 
                                padding: 10px; 
                                background: #f9f9f9; 
                                border-radius: 6px;
                                border-left: 3px solid #4CAF50;
                                font-size: 13px;
                                line-height: 1.6;
                                word-wrap: break-word;
                                color: #333;
                            ">
                                ${summary}
                            </div>
                        </div>
                        
                        <!-- Link Amazon rimosso -->
                    </div>
                `;

                marker.bindPopup(popupContent, {
                    maxWidth: 450,
                    className: 'custom-popup',
                });

                markers.push(marker);
                console.log(`  ✓ Marker #${idx + 1} aggiunto a [${lat}, ${lon}]`);
            } catch (error) {
                console.error('❌ Errore creazione marker per', props.name, ':', error);
            }
        });

        console.log(`🔍 Totale marker AI aggiunti alla mappa: ${markers.length}`);

        if (markers.length > 0) {
            try {
                const group = L.featureGroup(markers);
                console.log('✅ Bounds dei marker AI:', group.getBounds());
            } catch (e) {
                console.warn('Errore nel calcolo bounds:', e);
            }
        }

        // Cleanup quando il componente si smonta o features cambiano
        return () => {
            console.log('🧹 Rimuovendo', markers.length, 'marker AI dalla mappa');
            markers.forEach((m, i) => {
                try {
                    map.removeLayer(m);
                } catch (e) {
                    console.warn(`Errore rimozione marker #${i + 1}:`, e);
                }
            });
        };
    }, [features, map, summaries]);

    return null;
}